import * as Sentry from '@sentry/vue';

// TEMPORARY — remove once DNDPLAYBOOK-11X is traced.
//
// The report-only Content-Security-Policy (#2059) logs a blocked inline <style>
// for hundreds of users a day, and the browser's report can't say whose it is:
// the source it names is our own `insertBefore` patch (tolerantInsertBefore.ts),
// which every script inserting through it passes through, and the sample that
// `'report-sample'` asked for (#2085) arrives empty.
//
// So this records the JavaScript stack of whatever inserts a <style> without a
// nonce, and when the browser reports that element as a violation, sends the
// stack and the element to Sentry. Reported once per page load.

const callers = new WeakMap<Node, string>();

let reported = false;

const recordCaller = (node: Node): void => {
  if (node instanceof HTMLStyleElement && !node.nonce) {
    callers.set(node, new Error().stack ?? '');
  }
};

const reportViolation = (event: SecurityPolicyViolationEvent): void => {
  const style = event.target;

  if (reported || !(style instanceof HTMLStyleElement)) {
    return;
  }

  if (event.blockedURI !== 'inline' || !event.effectiveDirective.startsWith('style-src')) {
    return;
  }

  reported = true;

  Sentry.captureMessage('CSP blocked an inline <style>', {
    level: 'warning',
    tags: { csp: 'blocked-style' },
    extra: {
      caller: callers.get(style) ?? 'not inserted through appendChild or insertBefore',
      element: style.outerHTML.slice(0, 500),
      length: style.textContent?.length ?? 0,
      sample: event.sample,
      directive: event.effectiveDirective,
    },
  });
};

/**
 * Record who inserts each unnonced <style>, and report it to Sentry when the
 * Content-Security-Policy blocks it. Browser-only; the SSR entrypoint never calls it.
 */
export const installBlockedStyleReporting = (): void => {
  if (typeof Node === 'undefined' || typeof document === 'undefined') {
    return;
  }

  const appendChild = Node.prototype.appendChild;
  const insertBefore = Node.prototype.insertBefore;

  Node.prototype.appendChild = function <T extends Node>(node: T): T {
    recordCaller(node);

    return appendChild.call(this, node) as T;
  };

  Node.prototype.insertBefore = function <T extends Node>(node: T, child: Node | null): T {
    recordCaller(node);

    return insertBefore.call(this, node, child) as T;
  };

  document.addEventListener('securitypolicyviolation', reportViolation);
};
